<template>
  <div class="w-full">

    <!-- QR Code Modal -->
    <Dialog v-model:visible="showQRDialog" :draggable="false" modal
      :header="`QR kód - Stôl č. ${selectedTable?.tableNumber}`" position="top"
      :style="{ width: '100%', 'max-width': '500px' }">
      <div class="text-center">
        <div class="mb-4 text-gray-600">
          {{ restaurantPlace === 'BO' ? 'Hajcer Bošany' : 'Hajcer Partizánske' }}
        </div>

        <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" class="mx-auto border rounded-lg" alt="QR Code" />

        <div class="mt-4 text-sm text-gray-500">
          {{ qrCodeContent }}
        </div>

        <div class="flex space-x-2 justify-center mt-6">
          <Button @click="downloadQR" label="Stiahnuť QR" icon="pi pi-download" size="small" />
          <Button @click="printQR" label="Vytlačiť" icon="pi pi-print" size="small" severity="secondary" />
        </div>
      </div>
    </Dialog>

    <div class="flex justify-between w-full items-center">
      <div>
        <SelectButton v-model="restaurantPlace" :options="RESTAURANT_PLACES" aria-labelledby="basic" size="small"
          option-value="value" optionLabel="name" :disabled="loading" />
      </div>
      <Button @click="loadTables" :loading="loading" size="small" label="Načítať stoly z PLU" icon="pi pi-refresh"
        iconPos="right" />
    </div>

    <hr class="my-4">

    <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
      {{ error }}
    </div>

    <div v-if="loading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner text-4xl"></i>
      <div class="mt-2">Načítavam stoly...</div>
    </div>

    <div v-else-if="tables.length === 0" class="text-center py-8 text-gray-500">
      <i class="pi pi-table text-4xl"></i>
      <div class="mt-2">Žiadne stoly. Kliknite na "Načítať stoly z PLU".</div>
    </div>

    <DataTable v-else :value="tables" tableStyle="width:100%">
      <Column field="tableNumber" header="Číslo stola / Názov">
        <template #body="slotProps">
          <span class="font-bold text-lg">{{ getTableLabel(slotProps.data) }}</span>
        </template>
      </Column>

      <Column field="areaName" header="Priestor"></Column>
      <Column field="stoolQuantity" header="Počet miest">
        <template #body="slotProps">
          {{ slotProps.data.stoolQuantity || '-' }}
        </template>
      </Column>
      <Column field="id" header="PLU ID">
        <template #body="slotProps">
          <span class="text-gray-500 text-sm">{{ slotProps.data.id }}</span>
        </template>
      </Column>
      <Column header="QR kód">
        <template #body="slotProps">
          <Button @click="showQRModal(slotProps.data)" icon="pi pi-qrcode" label="QR" size="small" severity="info" />
        </template>
      </Column>
    </DataTable>

    <div v-if="tables.length > 0" class="mt-4 text-right">
      <Button @click="downloadAllQRCodes" :loading="downloadingAll" label="Stiahnuť všetky QR kódy"
        icon="pi pi-download" severity="secondary" />
    </div>

  </div>
</template>

<script setup>
import { RESTAURANT_PLACES } from "@/content/lists.js"
import { ref, watch, onMounted } from 'vue'
import QRCode from 'qrcode'

const restaurantPlace = ref('BO')
const tables = ref([])
const loading = ref(false)
const error = ref(null)

const showQRDialog = ref(false)
const selectedTable = ref(null)
const qrCodeDataUrl = ref('')
const qrCodeContent = ref('')

const downloadingAll = ref(false)

// Automaticke nacitanie stolov pri zmene prevadzky
watch(() => restaurantPlace.value, () => {
  loadTables()
})

// Nacitanie stolov pri vstupe na stranku
onMounted(() => {
  loadTables()
})

const loadTables = async () => {
  loading.value = true
  error.value = null
  tables.value = []

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/get-tables/${restaurantPlace.value}`
    )

    const data = await response.json()

    if (data.success) {
      tables.value = data.tables || []
      if (tables.value.length === 0) {
        error.value = 'Neboli nájdené žiadne stoly v PLU systéme.'
      }
    } else {
      error.value = data.error || 'Nepodarilo sa načítať stoly'
    }
  } catch (e) {
    console.error('Error loading tables:', e)
    error.value = 'Chyba pri komunikácii so serverom'
  } finally {
    loading.value = false
  }
}

const getTableLabel = (table) => {
  const number = table?.tableNumber
  const name = table?.tableName
  const hasNumber = number !== null && number !== undefined && number !== ''
  const hasName = name !== null && name !== undefined && name !== ''

  if (hasName) return String(name)
  if (hasNumber) return String(number)
  return '-'
}

const generateQRContent = (table) => {
  // Format: hajcer://table/{place}/{blueGastroId}/{tableNumberOrName}
  const tableLabel = getTableLabel(table)
  return `hajcer://table/${restaurantPlace.value}/${table.id}/${encodeURIComponent(tableLabel)}`
}

const showQRModal = async (table) => {
  selectedTable.value = table
  qrCodeContent.value = generateQRContent(table)

  try {
    qrCodeDataUrl.value = await QRCode.toDataURL(qrCodeContent.value, {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    })
    showQRDialog.value = true
  } catch (e) {
    console.error('Error generating QR code:', e)
    error.value = 'Nepodarilo sa vygenerovať QR kód'
  }
}

const downloadQR = async () => {
  if (!selectedTable.value || !qrCodeDataUrl.value) return

  const link = document.createElement('a')
  link.download = `stol-${selectedTable.value.tableNumber}-${restaurantPlace.value}.png`
  link.href = qrCodeDataUrl.value
  link.click()
}

const printQR = () => {
  const printWindow = window.open('', '_blank')
  printWindow.document.write(`
    <html>
      <head>
        <title>QR kód - Stôl č. ${selectedTable.value?.tableNumber}</title>
        <style>
          body { 
            text-align: center; 
            font-family: Arial, sans-serif;
            padding: 40px;
          }
          img { 
            max-width: 300px; 
            margin: 20px auto;
          }
          h1 { margin-bottom: 10px; }
          .restaurant { color: #666; margin-bottom: 20px; }
          .table-number { font-size: 48px; font-weight: bold; margin: 20px 0; }
        </style>
      </head>
      <body>
        <h1>Hajcer</h1>
        <div class="restaurant">${restaurantPlace.value === 'BO' ? 'Bošany' : 'Partizánske'}</div>
        <div class="table-number">Stôl č. ${selectedTable.value?.tableNumber}</div>
        <img src="${qrCodeDataUrl.value}" />
        <p>Naskenujte QR kód pre objednávku od stola</p>
      </body>
    </html>
  `)
  printWindow.document.close()
  printWindow.print()
}

const downloadAllQRCodes = async () => {
  if (tables.value.length === 0) return

  downloadingAll.value = true

  try {
    for (const table of tables.value) {
      const content = generateQRContent(table)
      const dataUrl = await QRCode.toDataURL(content, {
        width: 300,
        margin: 2
      })

      const link = document.createElement('a')
      link.download = `stol-${table.tableNumber}-${restaurantPlace.value}.png`
      link.href = dataUrl
      link.click()

      // Kratka pauza medzi stiahnutiami
      await new Promise(r => setTimeout(r, 200))
    }
  } catch (e) {
    console.error('Error downloading all QR codes:', e)
    error.value = 'Chyba pri sťahovaní QR kódov'
  } finally {
    downloadingAll.value = false
  }
}
</script>
